import * as Schema from "effect/Schema";

export const ModelTier = Schema.Literals(["fast", "standard", "deep"]);
export type ModelTier = typeof ModelTier.Type;
export const PublishModelTier = Schema.Struct({
  requestKey: Schema.String,
  expectedRevision: Schema.Int,
  tier: ModelTier,
  location: Schema.Literal("cloud"),
  provider: Schema.String,
  model: Schema.String,
  reasoningDefault: Schema.Literals(["none", "low", "medium", "high"]),
  maxOutputTokens: Schema.Int,
});
export type PublishModelTier = typeof PublishModelTier.Type;
export const SetManagedModelOffer = Schema.Struct({
  requestKey: Schema.String,
  expectedRevision: Schema.Int,
  provider: Schema.String,
  model: Schema.String,
  enabled: Schema.Boolean,
});
export type SetManagedModelOffer = typeof SetManagedModelOffer.Type;
const Price = Schema.Struct({
  revisionKey: Schema.String,
  currency: Schema.String,
  inputRateMicrosPerMillion: Schema.NullOr(Schema.Number),
  outputRateMicrosPerMillion: Schema.NullOr(Schema.Number),
  cacheReadRateMicrosPerMillion: Schema.NullOr(Schema.Number),
});
export const ModelReadiness = Schema.Struct({
  canRun: Schema.Boolean,
  credentials: Schema.Literals(["present-unverified", "unavailable"]),
  blockers: Schema.Array(
    Schema.Struct({
      code: Schema.String,
      message: Schema.String,
      remedy: Schema.Literals(["models", "model-access", "connections", "usage"]),
    }),
  ),
});
export type ModelReadiness = typeof ModelReadiness.Type;
export const ModelCatalog = Schema.Struct({
  schemaVersion: Schema.Literal("model-tiers-v1"),
  worldKey: Schema.String,
  revision: Schema.Int,
  canPublish: Schema.Boolean,
  /**
   * Neucleos Auto as this member's World offers it (backend modelTiers.js autoOffer): null with no route in effect;
   * else the models its route may answer with, in order, what the route promises, and whether it can run for this
   * member now. Absent from a World that predates Auto: the Desk then offers no Auto.
   */
  auto: Schema.optionalKey(Schema.NullOr(Schema.Struct({
    row: Schema.Literal("auto"),
    routeRevisionKey: Schema.String,
    models: Schema.Array(Schema.Struct({ provider: Schema.String, model: Schema.String })),
    contextTokens: Schema.NullOr(Schema.Int),
    maxOutputTokens: Schema.NullOr(Schema.Int),
    imageInput: Schema.Boolean,
    canRun: Schema.Boolean,
    reason: Schema.NullOr(Schema.String),
  }))),
  tiers: Schema.Array(
    Schema.Struct({
      tier: ModelTier,
      location: Schema.Literal("cloud"),
      row: Schema.Literals(["background", "standard", "best"]),
      provider: Schema.String,
      model: Schema.String,
      reasoningDefault: Schema.Literals(["none", "low", "medium", "high"]),
      maxOutputTokens: Schema.Int,
      priceRevisionKey: Schema.String,
      /**
       * Whether this tier's model reads images (World lib/managedModelImages.js,
       * the same answer as its image refusal). Absent from a World that does
       * not publish it: the Desk then assumes nothing and behaves as before.
       */
      imageInput: Schema.optionalKey(Schema.Boolean),
      revision: Schema.Int,
      effectiveAt: Schema.Number,
    }),
  ),
  connections: Schema.Array(
    Schema.Struct({
      provider: Schema.String,
      connectionKey: Schema.NullOr(Schema.String),
      kind: Schema.Literal("neucleos-managed-api"),
      status: Schema.String,
      credentialPresent: Schema.Boolean,
      entitled: Schema.Boolean,
      granted: Schema.Boolean,
      usable: Schema.Boolean,
      reason: Schema.NullOr(Schema.String),
    }),
  ),
  models: Schema.Array(
    Schema.Struct({
      provider: Schema.String,
      model: Schema.String,
      sold: Schema.Boolean,
      offerRevision: Schema.optionalKey(Schema.Int),
      reason: Schema.optionalKey(Schema.NullOr(Schema.String)),
      reasoningLevels: Schema.optionalKey(Schema.Array(Schema.String)),
      reasoningDefault: Schema.optionalKey(Schema.String),
      /** The provider-documented context window and output limit (World lib/managedModelLimits.js). */
      contextTokens: Schema.optionalKey(Schema.Int),
      maxOutputTokens: Schema.optionalKey(Schema.Int),
      /** Whether this model reads images (World lib/managedModelImages.js); absent from older Worlds. */
      imageInput: Schema.optionalKey(Schema.Boolean),
      price: Schema.NullOr(Price),
      usable: Schema.Boolean,
      readiness: Schema.optionalKey(ModelReadiness),
    }),
  ),
});
export type ModelCatalog = typeof ModelCatalog.Type;
/** The Desk's slug for Neucleos Auto (its World row is "auto"). */
export const NEUCLEOS_AUTO_SLUG = "neucleos/auto";

/**
 * Whether the World says a managed choice reads images: the same answer its
 * image refusal gives (World lib/managedModelImages.js). null when the World
 * does not say (an older World, an unknown slug, no catalog): the Desk then
 * assumes nothing. Shared by the composer (before Send) and the server (the
 * Desk's screenshot, W-59e53401).
 */
export function managedSlugReadsImages(catalog: ModelCatalog | null, slug: string): boolean | null {
  if (!catalog) return null;
  if (slug === NEUCLEOS_AUTO_SLUG) return catalog.auto ? catalog.auto.imageInput : null;
  const tier = /^neucleos\/(fast|standard|deep)$/.exec(slug)?.[1];
  if (tier) {
    const row = catalog.tiers.find((item) => item.tier === tier);
    if (!row) return null;
    if (row.imageInput !== undefined) return row.imageInput;
    return (
      catalog.models.find((item) => item.provider === row.provider && item.model === row.model)
        ?.imageInput ?? null
    );
  }
  const slash = slug.indexOf("/");
  if (slash <= 0) return null;
  const provider = slug.slice(0, slash);
  const model = slug.slice(slash + 1);
  return (
    catalog.models.find((item) => item.provider === provider && item.model === model)?.imageInput ??
    null
  );
}
